import React, { useState, useEffect, useCallback } from 'react';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdVisibility, 
  MdDownload, 
  MdRefresh, 
  MdCheck, 
  MdClose,
  MdPrint,
  MdFilterList,
  MdWarning,
  MdSearch
} from 'react-icons/md';
import styles from './BankDetails.module.scss';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';
import BankDetailsForm from './BankDetailsForm';
import BankDetailsView from './BankDetailsView';
import BankDetailsEdit from './BankDetailsEdit';

const BankDetails = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [bankData, setBankData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    office_name: '',
    account_head_name: '',
    official_email: '',
    bank_name: '',
    account_type: '',
    reference_code: '',
    is_active: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk operations
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Fetch bank details
  const fetchBankDetails = useCallback(async (page = 1, showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        grouped: 'true' // Use grouped view
      });

      // Add filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setBankData(data.results || data);
        setTotalPages(Math.ceil((data.count || data.length) / itemsPerPage));
        setCurrentPage(page);
      } else {
        throw new Error('Failed to fetch bank details');
      }
    } catch (error) {
      setErrorMessage('Error fetching bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, filters]);

  useEffect(() => {
    fetchBankDetails(1);
  }, [fetchBankDetails]);

  // Handle form success
  const handleFormSuccess = (result) => {
    setSuccessMessage(result.message);
    if (result.referenceCode) {
      setReferenceCode(result.referenceCode);
    }
    setShowForm(false);
    fetchBankDetails(1);
    
    // Clear success message after 8 seconds
    setTimeout(() => {
      setSuccessMessage('');
      setReferenceCode('');
    }, 8000);
  };

  // Handle item update
  const handleUpdate = async (result) => {
    try {
      setLoading(true);
      
      if (result.message) {
        setSuccessMessage(result.message);
        setShowEdit(false);
        setSelectedItem(null);
        fetchBankDetails(currentPage);
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to update bank details');
      }
    } catch (error) {
      setErrorMessage('Error updating bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle item deletion
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank detail?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/${id}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccessMessage('Bank details deleted successfully!');
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to delete bank details');
      }
    } catch (error) {
      setErrorMessage('Error deleting bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle group deletion
  const handleDeleteGroup = async (group) => {
    if (!window.confirm('Are you sure you want to delete this entire group? This will delete all bank accounts for this office.')) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/bulk_delete_group/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          office_name: group.office_name,
          account_head_name: group.account_head_name,
          official_email: group.official_email
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(result.message);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete group');
      }
    } catch (error) {
      setErrorMessage('Error deleting group: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete all bank details
  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL bank details? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/delete-all/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(result.message || 'All bank details deleted successfully!');
        fetchBankDetails(1);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete all bank details');
      }
    } catch (error) {
      setErrorMessage('Error deleting all bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle activation/deactivation
  const handleActivation = async (id, action) => {
    const actionText = action === 'activate' ? 'activate' : 'deactivate';
    const confirmMessage = action === 'activate' 
      ? 'Are you sure you want to activate this token for editing?'
      : 'Are you sure you want to deactivate this token?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const url = action === 'activate' 
        ? `${API_BASE_URL}/api/bank-details/${id}/activate_editing/`
        : `${API_BASE_URL}/api/bank-details/${id}/deactivate_editing/`;
      
      const data = action === 'activate' 
        ? { duration_hours: 24, admin_username: 'admin' }
        : { admin_username: 'admin' };

      const response = await makeAuthenticatedRequest(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        const message = action === 'activate' 
          ? `✅ Token activated successfully! ${result.message}`
          : `✅ Token deactivated successfully! ${result.message}`;
        setSuccessMessage(message);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${action} bank details`);
      }
    } catch (error) {
      setErrorMessage(`Error ${action}ing bank details: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel download
  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add filters to download
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/download-excel/?${params.toString()}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bank_details_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('Excel file downloaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to download Excel file');
      }
    } catch (error) {
      setErrorMessage('Error downloading Excel file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk operations
  const handleBulkActivation = async (action) => {
    if (selectedItems.length === 0) {
      setErrorMessage('Please select items for bulk operation');
      return;
    }

    try {
      setLoading(true);
      const referenceCodes = selectedItems.map(id => 
        bankData.find(item => item.id === id)?.reference_code
      ).filter(Boolean);

      const url = action === 'activate' 
        ? `${API_BASE_URL}/api/bank-details/bulk_activate/`
        : `${API_BASE_URL}/api/bank-details/bulk_deactivate/`;
      
      const data = action === 'activate' 
        ? { reference_code: referenceCodes[0], duration_hours: 24, admin_username: 'admin' }
        : { reference_code: referenceCodes[0], admin_username: 'admin' };

      const response = await makeAuthenticatedRequest(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(result.message);
        setSelectedItems([]);
        setSelectAll(false);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || `Failed to bulk ${action} bank details`);
      }
    } catch (error) {
      setErrorMessage(`Error bulk ${action}ing bank details: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle print view
  const handlePrintView = () => {
    // Use the current filtered data for printing
    const dataToPrint = bankData;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>बैंक खाता विवरण फारम</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .formTable { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            .formTable th, .formTable td { 
              border: 1px solid #333; 
              padding: 8px; 
              text-align: left; 
              vertical-align: middle;
            }
            .formTable th { 
              background-color: #f5f5f5; 
              font-weight: bold; 
              text-align: center;
            }
            .centerHeader { 
              text-align: center; 
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              font-weight: bold;
              font-size: 16px;
            }
            .note { 
              background-color: #fff3cd; 
              border: 1px solid #ffeaa7; 
              padding: 12px 16px; 
              border-radius: 6px; 
              color: #856404; 
              margin-bottom: 20px;
            }
            .note p { margin: 0; font-size: 14px; line-height: 1.4; }
            @media print {
              body { margin: 0; }
              .formTable { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1 style="text-align: center; margin-bottom: 30px;">बैंक खाता विवरण फारम</h1>
          ${dataToPrint.map((group, groupIndex) => `
            <table class="formTable">
              <tbody>
                <tr>
                  <th colspan="4" class="centerHeader">
                    मध्यपश्चिम विश्वविद्यालय
                  </th>
                </tr>
                <tr>
                  <td><strong>कार्यालयको नाम.-</strong></td>
                  <td colspan="3">${group.office_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td><strong>लेखा प्रमुखको नाम</strong></td>
                  <td colspan="3">${group.account_head_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td><strong>लेखा प्रमुखको <b>official Mail ID</b></strong></td>
                  <td colspan="3">${group.official_email || 'N/A'}</td>
                </tr>
                <tr>
                  <th colspan="4" class="centerHeader">
                    कार्यालयको नाममा सञ्चालित सम्पूर्ण बैक खाताको विवरण
                  </th>
                </tr>
                <tr>
                  <th>क्र.सं.</th>
                  <th>बैंक नाम</th>
                  <th>खाताको प्रकार</th>
                  <th>खाता नं.</th>
                </tr>
                ${group.bank_accounts.map((account, accountIndex) => `
                  <tr>
                    <td style="text-align: center; font-weight: bold;">${accountIndex + 1}</td>
                    <td>${account.bank_name || ''}</td>
                    <td>${account.account_type || ''}</td>
                    <td>${account.account_number || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="note">
              <p><b>नोट:-</b> सम्पूर्ण नमस्कार, माथि उल्लेखित विवरण अनिवार्यरुपमा भर्नुहोस् जानकारी गराइन्छ ।</p>
            </div>
            ${groupIndex < dataToPrint.length - 1 ? '<div style="page-break-after: always;"></div>' : ''}
          `).join('')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Handle download Excel for grouped view
  const handleDownloadGroupedExcel = async () => {
    try {
      setLoading(true);
      
      // Build query parameters with filters
      const params = new URLSearchParams({
        grouped: 'true'
      });

      // Add filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/download_excel/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bank_details_grouped.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('Grouped Excel file downloaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to download grouped Excel file');
      }
    } catch (error) {
      setErrorMessage('Error downloading grouped Excel file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchBankDetails(1);
  };

  const clearFilters = () => {
    setFilters({
      office_name: '',
      account_head_name: '',
      official_email: '',
      bank_name: '',
      account_type: '',
      reference_code: '',
      is_active: ''
    });
    fetchBankDetails(1);
  };

  // Handle item selection
  const handleItemSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      setSelectedItems(bankData.map(item => item.id));
      setSelectAll(true);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    fetchBankDetails(page);
  };

  // Handle view/edit actions
  const handleView = (item) => {
    setSelectedItem(item);
    setShowView(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEdit(true);
  };



  return (
    <div className={styles.bankDetailsContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>🏦 Bank Details Management</h1>
          <p>Manage bank account information with reference codes and editing controls</p>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.formLinkButton}
            onClick={() => setShowForm(true)}
            disabled={loading}
            title="बैंक खाता विवरण फारम - बृहत् दृश्यमा खोल्नुहोस्"
          >
            📋 बैंक खाता विवरण फारम
          </button>
          <button 
            className={styles.addButton}
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            <MdAdd /> Add New Bank Details
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className={styles.successMessage}>
          <MdCheck /> {successMessage}
          {referenceCode && (
            <div className={styles.referenceCode}>
              <strong>Reference Code: {referenceCode}</strong>
              <p>Save this code for future reference and corrections!</p>
            </div>
          )}
        </div>
      )}
      
      {errorMessage && (
        <div className={styles.errorMessage}>
          <MdWarning /> {errorMessage}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersSection}>
        <div className={styles.filtersHeader}>
          <button 
            className={styles.filterToggle}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList /> Filters
          </button>
          <div className={styles.filterActions}>
            <button onClick={applyFilters} disabled={loading}>
              <MdSearch /> Apply
            </button>
            <button onClick={clearFilters} disabled={loading}>
              Clear
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className={styles.filtersGrid}>
            <div className={styles.filterGroup}>
              <label>कार्यालयको नाम</label>
              <input
                type="text"
                value={filters.office_name}
                onChange={(e) => handleFilterChange('office_name', e.target.value)}
                placeholder="Search by office name"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>लेखा प्रमुखको नाम</label>
              <input
                type="text"
                value={filters.account_head_name}
                onChange={(e) => handleFilterChange('account_head_name', e.target.value)}
                placeholder="Search by account head name"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>लेखा प्रमुखको official Mail ID</label>
              <input
                type="text"
                value={filters.official_email}
                onChange={(e) => handleFilterChange('official_email', e.target.value)}
                placeholder="Search by official email"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>बैंकको नाम</label>
              <input
                type="text"
                value={filters.bank_name}
                onChange={(e) => handleFilterChange('bank_name', e.target.value)}
                placeholder="Search by bank name"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>खाताको प्रकार</label>
              <input
                type="text"
                value={filters.account_type}
                onChange={(e) => handleFilterChange('account_type', e.target.value)}
                placeholder="Search by account type"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>Reference Code</label>
              <input
                type="text"
                value={filters.reference_code}
                onChange={(e) => handleFilterChange('reference_code', e.target.value)}
                placeholder="Search by reference code"
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label>Editing Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Active for Editing</option>
                <option value="false">Not Editable</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedItems.length} item(s) selected</span>
          <div className={styles.bulkButtons}>
            <button 
              onClick={() => handleBulkActivation('activate')}
              disabled={loading}
              className={styles.activateButton}
            >
              <MdCheck /> Activate for Editing
            </button>
            <button 
              onClick={() => handleBulkActivation('deactivate')}
              disabled={loading}
              className={styles.deactivateButton}
            >
              <MdClose /> Deactivate Editing
            </button>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className={styles.actionsBar}>
        <div className={styles.actionsLeft}>
          <button 
            onClick={() => fetchBankDetails(currentPage)}
            disabled={loading}
            className={styles.refreshButton}
          >
            <MdRefresh /> Refresh
          </button>
          <button 
            onClick={handlePrintView}
            disabled={loading}
            className={styles.printButton}
          >
            <MdPrint /> Print View
          </button>
          <button 
            onClick={handleDownloadGroupedExcel}
            disabled={loading}
            className={styles.downloadButton}
          >
            <MdDownload /> Download Excel
          </button>
          <button 
            onClick={handleDeleteAll}
            disabled={loading}
            className={styles.deleteAllButton}
            title="Delete All Bank Details"
          >
            🗑️ Delete All
          </button>
        </div>
        
        <div className={styles.actionsRight}>
          <span className={styles.itemCount}>
            {bankData.length} of {totalPages * itemsPerPage} items
          </span>
        </div>
      </div>

      {/* Data Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading bank details...</p>
          </div>
        ) : (
          <div className={styles.groupedDataContainer}>
            {bankData.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.groupedItem}>
                {/* Office Information Header */}
                <div className={styles.officeHeader}>
                  <div className={styles.officeInfo}>
                    <div className={styles.officeDetails}>
                      <div className={styles.officeField}>
                        <strong>कार्यालयको नाम.-</strong> {group.office_name || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>लेखा प्रमुखको नाम.-</strong> {group.account_head_name || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>लेखा प्रमुखको official Mail ID.-</strong> {group.official_email || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>टोकन स्थिति:</strong> 
                        <span className={group.is_editable ? styles.activeStatus : styles.inactiveStatus}>
                          {group.is_editable ? 'सक्रिय (सम्पादन गर्न सकिन्छ)' : 'निष्क्रिय (सम्पादन गर्न मिल्दैन)'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.groupActions}>
                    <button
                      onClick={() => handleView(group)}
                      className={styles.viewButton}
                      title="View Details"
                    >
                      <MdVisibility />
                    </button>
                    <button
                      onClick={() => handleEdit(group)}
                      className={styles.editButton}
                      disabled={!group.is_editable}
                      title={group.is_editable ? "Edit Details" : "Not Editable"}
                    >
                      <MdEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className={styles.deleteButton}
                      title="Delete Entire Group"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </div>

                {/* Bank Accounts Table */}
                <div className={styles.bankAccountsSection}>
                  <h4>कार्यालयको नाममा सञ्चालित सम्पूर्ण बैक खाताको विवरण</h4>
                  <table className={styles.bankAccountsTable}>
                    <thead>
                      <tr>
                        <th>क्र.सं.</th>
                        <th>बैंक नाम</th>
                        <th>खाताको प्रकार</th>
                        <th>खाता नं.</th>
                        <th>Reference Code</th>
                        <th>Status</th>
                                                 <th title="Click the buttons to activate/deactivate tokens for editing">टोकन स्थिति / नियन्त्रण ℹ️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.bank_accounts.map((account, accountIndex) => (
                        <tr key={account.id} className={account.is_editable ? styles.editable : ''}>
                          <td>{accountIndex + 1}</td>
                          <td>{account.bank_name}</td>
                          <td>{account.account_type}</td>
                          <td>{account.account_number}</td>
                          <td className={styles.referenceCode}>
                            <strong>{group.reference_code}</strong>
                          </td>
                          <td>
                            <span className={account.is_editable ? styles.editableBadge : styles.notEditableBadge}>
                              {account.is_editable ? 'Editable' : 'Not Editable'}
                            </span>
                          </td>
                                                     <td>
                             <div className={styles.tokenStatusContainer}>
                               <span className={account.is_editable ? styles.activeStatus : styles.inactiveStatus}>
                                 {account.is_editable ? 'सक्रिय (सम्पादन गर्न सकिन्छ)' : 'निष्क्रिय (सम्पादन गर्न मिल्दैन)'}
                               </span>
                               <div className={styles.tokenActions}>
                                 {account.is_editable ? (
                                   <button
                                     onClick={() => handleActivation(account.id, 'deactivate')}
                                     className={styles.deactivateTokenButton}
                                     title="Deactivate Token"
                                     disabled={loading}
                                   >
                                     <MdClose />
                                   </button>
                                 ) : (
                                   <button
                                     onClick={() => handleActivation(account.id, 'activate')}
                                     className={styles.activateTokenButton}
                                     title="Activate Token"
                                     disabled={loading}
                                   >
                                     <MdCheck />
                                   </button>
                                 )}
                               </div>
                             </div>
                           </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Reference Code */}
                <div className={styles.referenceCodes}>
                  <strong>Reference Code:</strong> {group.reference_code}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          
          <div className={styles.pageNumbers}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={page === currentPage ? styles.activePage : ''}
                disabled={loading}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <BankDetailsForm
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {showView && selectedItem && (
        <BankDetailsView
          item={selectedItem}
          onClose={() => {
            setShowView(false);
            setSelectedItem(null);
          }}
        />
      )}

      {showEdit && selectedItem && (
        <BankDetailsEdit
          item={selectedItem}
          onSubmit={handleUpdate}
          onClose={() => {
            setShowEdit(false);
            setSelectedItem(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

export default BankDetails;
